<template>
<div v-loading="dataLoading" class="alert-detail-wrapper">
  <div v-if="detailsData" class="alert-detail">
    <div class="detail-top">
      <div class="status-title">{{$t('message.tableCommon.status')}}</div>
      <div class="status-step-box">
        <div v-for="(item, index) in stepList" :key="item.value" class="status-step-item">
          <div v-if="index >= currentIndex" class="status-step-dot" :class="{current: currentIndex === index}">{{index + 1}}</div>
          <div v-else class="status-step-dot finished iconfont icon-wancheng-buzhou"></div>
          <div class="status-step-label">{{item.label}}</div>
          <div v-if="index < stepList.length - 1" class="status-step-line"></div>
        </div>
      </div>
    </div>
    <details-component :details-condition="detailCondition">
    </details-component>
  </div>
</div>
</template>
<script>

import DetailsComponent from '@/components/base-details/details.vue';
import { ShowWarningMessage, ShowSuccessMessage } from '@/components/modal/tips/message-tips';
import GlobalData from '@/utils/global/index';
import { getEnantiomericValue, initSelectedData } from '@/utils/tools/constant';
import { isNullForDisplay } from '@/utils/tools/utils';
import { queryEthocaAlertDetail } from '@/views/risk/api/ethoca';

export default {
  components: {
    DetailsComponent,
  },
  data() {
    return {
      dataLoading: true,
      id: '',
      outcomeStatusList: [],
      refundStatusList: [],
      detailCondition: [],
      detailsData: null,
      stepList: [{
        label: this.$t('message.alert.alertReceived'),
        value: '0',
      }, {
        label: this.$t('message.alert.outcomeSubmitted'),
        value: '1',
      }],
      currentIndex: 1,
    };
  },
  methods: {
    async initData() {
      this.id = this.$route.query.id;
      const dict = await GlobalData.getDictList(['ETHOCA_ACW_OUTCOME', 'ETHOCA_ACW_REFUND_STATUS']);
      this.outcomeStatusList = initSelectedData(dict.ETHOCA_ACW_OUTCOME, 'description', 'value', false);
      this.refundStatusList = initSelectedData(dict.ETHOCA_ACW_REFUND_STATUS, 'description', 'value', false);
      await this.initDetailsAll();
    },
    getAlertInfoTemplate(allData) {
      return {
        topTitle: 'message.alert.info',
        type: 'default',
        listTemplate: [
          {
            titleBar: { title: 'message.alert.ethocaId' },
            key: 'ethocaId',
          },
          {
            titleBar: { title: 'message.alert.alertDate' },
            key: 'alertTime',
          },
          {
            titleBar: { title: 'message.alert.alertAge' },
            key: 'alertAge',
          },
          {
            titleBar: { title: 'message.alert.initiatedBy' },
            key: 'initiatedBy',
          },
          {
            titleBar: { title: 'message.alert.liability' },
            key: 'liability',
          },
        ],
        data: allData,
      };
    },
    getMerchantInfoTemplate(allData) {
      return {
        topTitle: 'message.alert.merchantIdentifier',
        type: 'default',
        listTemplate: [
          {
            titleBar: { title: 'message.alert.billDesc' },
            key: 'billDesc',
          },
          {
            titleBar: { title: 'message.alert.ethocaMerchantId' },
            key: 'ethocaMerchantId',
          },
          {
            titleBar: { title: 'message.alert.resellerSubMerchantId' },
            key: 'resellerSubMerchantId',
          },
          {
            titleBar: { title: 'message.alert.integratorMemberId' },
            key: 'integratorMemberId',
          },
        ],
        data: allData,
      };
    },
    getTransactionTemplate(allData) {
      return {
        topTitle: 'message.alert.transactionInfo',
        type: 'default',
        listTemplate: [
          {
            titleBar: { title: 'message.alert.issuer' },
            key: 'issuer',
          },
          {
            titleBar: { title: 'message.alert.cardNumber' },
            key: 'cardNumber',
          },
          {
            titleBar: { title: 'message.alert.arn' },
            key: 'arn',
          },
          {
            titleBar: { title: 'message.alert.authTime' },
            key: 'txnTime',
          },
          {
            titleBar: { title: 'message.alert.MCC' },
            key: 'mcc',
          },
          {
            titleBar: { title: 'message.alert.amount' },
            key: 'txnAmount',
          },
          {
            titleBar: { title: 'message.alert.currency' },
            key: 'txnCurrency',
          },
          {
            titleBar: { title: 'message.alert.transactionType' },
            key: 'transactionType',
          },
          {
            titleBar: { title: 'message.alert.authCode' },
            key: 'authCode',
          },
          {
            titleBar: { title: 'message.alert.transactionId' },
            key: 'transactionId',
          },
        ],
        data: allData,
      };
    },
    getOutcomeInfoTemplate(allData) {
      return {
        topTitle: 'message.alert.outcomeInfo',
        type: 'default',
        listTemplate: [
          {
            titleBar: { title: 'message.alert.outcome' },
            key: 'outcome',
            textFunc: (data) => data.outcome ? getEnantiomericValue(this.outcomeStatusList, data.outcome) : '--',
          },
          {
            titleBar: { title: 'message.alert.refundStatus' },
            key: 'refundStatus',
            textFunc: (data) => data.refundStatus ? getEnantiomericValue(this.refundStatusList, data.refundStatus) : '--',
          },
          {
            titleBar: { title: 'message.alert.outcomeUpdatedTime' },
            key: 'outcomeUpdatedTime',
          },
        ],
        data: allData,
      };
    },
    getChargebackInfoTemplate(allData) {
      return {
        topTitle: 'message.alert.chargebackInfo',
        type: 'default',
        listTemplate: [
          {
            titleBar: { title: 'message.alert.chargebackReasonCode' },
            key: 'chargebackReasonCode',
          },
          {
            titleBar: { title: 'message.alert.chargebackAmount' },
            key: 'chargebackAmount',
          },
          {
            titleBar: { title: 'message.alert.chargebackCurrency' },
            key: 'chargebackCurrency',
          },
        ],
        data: allData,
      };
    },
    async initDetailsAll() {
      this.dataLoading = true;
      try {
        this.detailCondition.length = 0;
        this.detailCondition.push(this.getAlertInfoTemplate({}));
        this.detailCondition.push(this.getMerchantInfoTemplate({}));
        this.detailCondition.push(this.getTransactionTemplate({}));
        this.detailCondition.push(this.getChargebackInfoTemplate({}));
        this.detailCondition.push(this.getOutcomeInfoTemplate({}));
        const alertInfo = await queryEthocaAlertDetail(this.id);
        const data = {
          ...alertInfo,
        };
        this.detailCondition.length = 0;
        this.detailsData = data;
        this.detailCondition.push(this.getAlertInfoTemplate(data));
        this.detailCondition.push(this.getMerchantInfoTemplate(data));
        this.detailCondition.push(this.getTransactionTemplate(data));
        this.detailCondition.push(this.getChargebackInfoTemplate(data));
        this.detailCondition.push(this.getOutcomeInfoTemplate(data));

        if (data.submitOutcomeStatus !== 'PENDING_OUTCOME') {
          this.currentIndex = 2;
        }
      } catch (err) {
        //
      }
      this.dataLoading = false;
    },
  },
  mounted() {
    this.initData();
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/variables.scss';

.alert-detail-wrapper {
  min-height: 500px;
}

.alert-detail {
  ::v-deep #details .bd-details-wrap {
    &:first-child {
      border-top: none;
    }
    .el-card__body {
      padding-left: 0;
      padding-right: 0;
    }
  }
}

.detail-top {
  margin-bottom: 30px;
  padding-top: 30px;
  .title-txt {
    font-size: 14px;
    font-family: Avenir-Book, Avenir;
    font-weight: normal;
    color: $color-text-primary;
    line-height: 19px;
  }

  .amount-box {
    display: flex;
    align-items: center;
    margin-top: 7px;
    margin-bottom: 15px;
    font-family: Avenir-Heavy, Avenir;
    color: $color-text-primary;
    font-weight: 800;
    .amount-txt {
      font-size: 30px;
      line-height: 41px;
      margin-right: 15px;
    }

    .currency-icon {
      width: 26px;
      margin-right: 38px;
    }

    .status {
      font-size: 14px;
      line-height: 30px;
      padding: 0 8px;
      height: 30px;
      border-radius: 4px;
      background: rgba(252,100,45,0.15);

      &-S {
        background: rgba(0,221,40,0.15);
      }

      &-P, &-U {
        background:rgba(255,178,9,0.15);
      }

      &-N {
        background:#F1F5F7;
      }
    }
  }
  .status-title {
    font-weight: 800;
    font-size: 16px;
    color: $color-text-primary;
    line-height: 25px;
    margin-bottom: 20px;
  }
  .status-step-box {
    display: flex;
    align-items: center;

    .status-step-item {
      display: flex;
      align-items: center;
      width: 216px;
      margin-right: 12px;

      .status-step-dot {
        width: 24px;
        height: 24px;
        background-color: $color-border-txn;
        color: $color-text-primary;
        border-radius: 50%;
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        text-align: center;
        &.current {
          background: $color-text-default-button;
          color: white;
        }
        &.finished {
          font-size: 20px;
          background-color: #E8F3FF;
        }
      }

      .status-step-label {
        margin-left: 12px;
        margin-right: 12px;
        font-weight: 500;
        font-size: 14px;
        color: $color-text-primary;
        line-height: 22px;
      }

      .status-step-line {
        flex: 1;
        border-top: 1px solid #E1E3E4;
      }
    }
  }
}
</style>
